/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
/* eslint-disable jsx-a11y/role-has-required-aria-props */

import {
    DropIndicatorProps as ReactAriaDropIndicatorProps,
    ListDropTargetDelegate,
    useDropIndicator,
    useDroppableCollection,
    useDraggableCollection,
    useDraggableItem,
    DroppableCollectionReorderEvent,
} from "@react-aria/dnd";
import { useFocusRing } from "@react-aria/focus";
import { AriaListBoxProps, useListBox, useOption } from "@react-aria/listbox";
import { ListKeyboardDelegate } from "@react-aria/selection";
import { mergeProps } from "@react-aria/utils";
import React, { CSSProperties, useRef } from "react";
import {
    DraggableCollectionState,
    DroppableCollectionState,
    ListProps,
    ListState,
    Node,
    useDraggableCollectionState,
    useDroppableCollectionState,
    useListData,
    useListState,
} from "react-stately";
import cn from "classnames";

import "./ReorderableList.scss";

type Props<T extends { id: string }> = Pick<
    ListProps<T>,
    "children" | "selectedKeys" | "onSelectionChange" | "selectionMode"
> &
    Pick<AriaListBoxProps<T>, "aria-labelledby"> & {
        initialItems: T[];
        onReorder?: (newOrder: T[]) => void;
        className?: string;
        classNames?: Partial<{
            dragging: string;
        }>;
        accentColor?: string;
    };

const ReorderableList = <T extends { id: string }>({
    initialItems,
    children,
    "aria-labelledby": ariaLabelledBy,
    className,
    selectionMode,
    selectedKeys,
    onSelectionChange,
    onReorder,
    classNames,
    accentColor,
}: Props<T>) => {
    const list = useListData({
        initialItems,
    });
    const onAriaReorder = (e: DroppableCollectionReorderEvent) => {
        if (e.target.dropPosition === "before") {
            list.moveBefore(e.target.key, e.keys);
        } else if (e.target.dropPosition === "after") {
            list.moveAfter(e.target.key, e.keys);
        }
        if (onReorder) {
            const draggedItems = list.items.filter((item) =>
                e.keys.has(item.id)
            );
            const listWithoutDraggedItems = list.items.filter(
                (item) => !e.keys.has(item.id)
            );
            const targetIndex = listWithoutDraggedItems.findIndex(
                (item) => e.target.key === item.id
            );
            if (e.target.dropPosition === "before")
                onReorder([
                    ...listWithoutDraggedItems.slice(0, targetIndex),
                    ...draggedItems,
                    ...listWithoutDraggedItems.slice(targetIndex),
                ]);
            else if (e.target.dropPosition === "after")
                onReorder([
                    ...listWithoutDraggedItems.slice(0, targetIndex + 1),
                    ...draggedItems,
                    ...listWithoutDraggedItems.slice(targetIndex + 1),
                ]);
        }
    };

    const listState = useListState({
        items: list.items,
        children,
        selectionMode: getSelectionMode(
            selectionMode,
            selectedKeys,
            onSelectionChange
        ),
        selectedKeys,
        onSelectionChange,
    });
    const listRef = useRef<HTMLOListElement>(null);
    const { listBoxProps } = useListBox(
        {
            "aria-labelledby": ariaLabelledBy,
            items: list.items,
            // Prevent dragging from changing selection.
            shouldSelectOnPressUp: true,
        },
        listState,
        listRef
    );

    const dropState = useDroppableCollectionState({
        onReorder: onAriaReorder,
        collection: listState.collection,
        selectionManager: listState.selectionManager,
    });

    const { collectionProps } = useDroppableCollection(
        {
            onReorder: onAriaReorder,
            // Provide drop targets for keyboard and pointer-based drag and drop.
            keyboardDelegate: new ListKeyboardDelegate(
                listState.collection,
                listState.disabledKeys,
                listRef
            ),
            dropTargetDelegate: new ListDropTargetDelegate(
                listState.collection,
                listRef
            ),
        },
        dropState,
        listRef
    );

    const dragState = useDraggableCollectionState({
        collection: listState.collection,
        selectionManager: listState.selectionManager,
        getItems: (keys) => {
            return [...keys].map((key) => {
                const item = listState.collection.getItem(key);
                if (!item) throw new Error("Item not found");
                return {
                    "text/plain": item.textValue,
                };
            });
        },
    });
    useDraggableCollection(
        { items: list.items, children, "aria-labelledby": ariaLabelledBy },
        dragState,
        listRef
    );

    return (
        <ol
            {...mergeProps(listBoxProps, collectionProps)}
            className={cn(
                className,
                listBoxProps.className,
                collectionProps.className
            )}
            ref={listRef}
            style={
                {
                    "--accent-color": accentColor,
                } as CSSProperties
            }
        >
            {[...listState.collection].map((item) => (
                <ReorderableOption
                    key={item.key}
                    item={item}
                    state={listState}
                    dragState={dragState}
                    dropState={dropState}
                    classNames={classNames}
                />
            ))}
        </ol>
    );
};
export default ReorderableList;

type ReorderableOptionProps<T extends { id: string }> = {
    dropState: DroppableCollectionState;
    dragState: DraggableCollectionState;
    item: Node<T>;
    state: ListState<T>;
    classNames?: Props<T>["classNames"];
};
const ReorderableOption = <T extends { id: string }>({
    item,
    state,
    dragState,
    dropState,
    classNames,
}: ReorderableOptionProps<T>) => {
    const ref = useRef<HTMLLIElement>(null);
    const { optionProps } = useOption({ key: item.key }, state, ref);
    const { focusProps } = useFocusRing();

    // Register the item as a drag source.
    const { dragProps } = useDraggableItem(
        {
            key: item.key,
        },
        dragState
    );

    return (
        <>
            <DropIndicator
                target={{ type: "item", key: item.key, dropPosition: "before" }}
                dropState={dropState}
            />
            <li
                {...mergeProps(optionProps, dragProps, focusProps)}
                ref={ref}
                className={cn(
                    "reorderable-option__list-item",
                    {
                        "--dragging": dragState.isDragging(item.key),
                    },
                    classNames?.dragging && {
                        [classNames.dragging]: dragState.isDragging(item.key),
                    }
                )}
            >
                {item.rendered}
            </li>
            {state.collection.getKeyAfter(item.key) == null && (
                <DropIndicator
                    target={{
                        type: "item",
                        key: item.key,
                        dropPosition: "after",
                    }}
                    dropState={dropState}
                />
            )}
        </>
    );
};

type DropIndicatorProps = ReactAriaDropIndicatorProps & {
    dropState: DroppableCollectionState;
};
const DropIndicator = ({
    dropState,
    ...reactAriaDropIndicatorProps
}: DropIndicatorProps) => {
    const ref = React.useRef(null);
    const { dropIndicatorProps, isHidden } = useDropIndicator(
        reactAriaDropIndicatorProps,
        dropState,
        ref
    );

    if (isHidden) return null;

    return (
        <li
            {...dropIndicatorProps}
            role="option"
            ref={ref}
            className="drop-indicator"
        />
    );
};

const getSelectionMode = (
    selectionMode: ListProps<any>["selectionMode"],
    selectedKeys: ListProps<any>["selectedKeys"],
    onSelectionChange: ListProps<any>["onSelectionChange"]
): ListProps<any>["selectionMode"] => {
    if (selectionMode) return selectionMode;
    // default to single if props are passed but no selection mode is passed
    if (selectedKeys && onSelectionChange) return "single";
    return "none";
};
